import { useMemo } from 'react'
import { Count } from '@/types/count'
import { DocumentRowValue } from '@/types/documents'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'
import { getList, updateList } from '../request-wrappers'

const service = buildService<DocumentRowValue>({
  pathCategory: 'document_row_values',
})

export function useGetDocumentRowValue({
  id,
  ...options
}: UseGetDetailOptions<DocumentRowValue>) {
  const query = useGetDetail<DocumentRowValue>({
    id,
    queryKey: queryKeys.documentRowValues.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    documentRowValue: query.data,
    ...query,
  }
}

export function useCreateDocumentRowValue(
  options: UseCreateDetailOptions<DocumentRowValue> = {},
) {
  const mutation = useCreateDetail<DocumentRowValue>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createDocumentRowValue: mutation.mutate,
    createDocumentRowValueAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateDocumentRowValue(
  options: UseUpdateDetailOptions<DocumentRowValue> = {},
) {
  const mutation = useUpdateDetail<DocumentRowValue>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateDocumentRowValue: mutation.mutate,
    updateDocumentRowValueAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocumentRowValue(
  options: UseDeleteDetailOptions = {},
) {
  const mutation = useDeleteDetail<DocumentRowValue>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteDocumentRowValue: mutation.mutate,
    deleteDocumentRowValueAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetDocumentRowValues({
  filters,
  ...options
}: UseGetListOptions<DocumentRowValue> = {}) {
  const query = useGetList<DocumentRowValue>({
    filters,
    queryKey: queryKeys.documentRowValues.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    documentRowValues: query.allData,
    ...query,
  }
}

export function useUpdateDocumentRowValues(
  options: UseUpdateListOptions<DocumentRowValue> = {},
) {
  const mutation = useUpdateList<DocumentRowValue>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateDocumentRowValues: mutation.mutate,
    updateDocumentRowValuesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocumentRowValues({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<DocumentRowValue>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteDocumentRowValues: mutation.mutate,
    deleteDocumentRowValuesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Document Row Values With Cleanup
 * Used to create document rows values with additional cleanup on backend.
 */
export function useCreateUpdateDocumentRowValuesWithCleanup({
  workflowId,
  ...options
}: UseUpdateListOptions<DocumentRowValue> & { workflowId: string }) {
  const mutation = useUpdateList<DocumentRowValue>({
    serviceFn: (serviceFnOptions) => {
      return updateList<DocumentRowValue>({
        url: '/v2/pd/document_row_values/apply_with_cleanup',
        ...serviceFnOptions,
        filters: {
          ...serviceFnOptions.filters,
          workflow_id: workflowId,
        },
      })
    },
    ...options,
  })

  return {
    createOrUpdateDocumentRowValues: mutation.mutate,
    createOrUpdateDocumentRowValuesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Get Documents Count
 * Used to get the count of documents.
 */
export function useGetDocumentRowValuesCount({
  filters,
  ...options
}: UseGetListOptions<Count> = {}) {
  const updatedFilters = useMemo(
    () => ({
      'api-consistency': 'weak',
      ...filters, // Allow api-consistency to be overridden
    }),
    [filters],
  )
  const query = useGetList<Count>({
    filters: updatedFilters,
    queryKey: queryKeys.documentRowValues.list({ filters: updatedFilters }, [
      'count',
    ]),
    serviceFn: (serviceFnOptions) => {
      return getList<Count>({
        url: '/v2/pd/document_row_values/~count',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    documentRowValuesCount: query.data?.pages[0].count || 0,
    groupedCounts: query.allData,
    ...query,
  }
}
